import { useRef } from 'react';
import styled from 'styled-components';
import { useScene } from '../../hooks';
import { useEditor } from '../../hooks/useEditor';
import { TopbarButtons } from './topbarButtons/TopbarButtons';
import { MenuItem, TopbarMenu } from './topbarMenu/TopbarMenu';

import demoThemeJSON from '../../defaultThemeConfigs/DemoTheme.json';
import newUIThemeJSON from '../../defaultThemeConfigs/NewUITheme.json';

import configAPIJSON from '../../configAPI.json';
const configAPI = JSON.parse(JSON.stringify(configAPIJSON));
const ENDPOINTS = configAPI.endpoints;

export const TopbarContent = () => {
  const scene = useScene();
  const { userSession, setUI, launcherThemeSession } = useEditor();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const menuData: MenuItem[] = [
    {
      label: 'File',
      items: [
        {
          label: 'Save',
          onClick: () => scene.saveThemeVersion(),
        },
        { label: 'New', onClick: () => scene.newTheme() },
        {
          label: 'Open',
          items: [
            {
              label: 'New UI Theme',
              onClick: () => {
                scene.importLauncherConfigJSON(JSON.stringify(newUIThemeJSON));
                scene.setSelectedElementIDs([]);
              },
            },
            {
              label: 'DEMO Theme',
              onClick: () => {
                scene.importLauncherConfigJSON(JSON.stringify(demoThemeJSON));
                scene.setSelectedElementIDs([]);
              },
            },
          ],
        },
        {
          label: 'Version History',
          onClick: () => setUI((UI) => ({ ...UI, isVersionModalOpen: true })),
        },
        {
          separator: true,
        },
        {
          label: 'Import',
          items: [
            {
              label: 'Paste JSON from clipboard',
              onClick: () => {
                const input = prompt('Please paste the config JSON');
                if (input !== null && input !== '') {
                  scene.importLauncherConfigJSON(input);
                }
              },
            },
            {
              label: 'Upload JSON file',
              onClick: () => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              },
            },
          ],
        },
        {
          label: 'Export',
          items: [
            {
              label: 'Copy JSON to clipboard',
              onClick: () => {
                const config = scene.exportLauncherConfigJSON();
                navigator.clipboard.writeText(config);
              },
            },
            {
              label: 'Download JSON file',
              onClick: () => {
                const config = scene.exportLauncherConfigJSON();
                const dataStr =
                  'data:text/json;charset=utf-8,' + encodeURIComponent(config);
                const downloadAnchorNode = document.createElement('a');
                downloadAnchorNode.setAttribute('href', dataStr);
                downloadAnchorNode.setAttribute(
                  'download',
                  'launcherConfig.json'
                );
                document.body.appendChild(downloadAnchorNode); // required for firefox
                downloadAnchorNode.click();
                downloadAnchorNode.remove();
              },
            },
          ],
        },
        {
          separator: true,
        },
        {
          label: 'Open Theme Page',
          rightSlot: <i className="icon-link-ext" />,
          onClick: () =>
            window.open(
              `${ENDPOINTS.themesPage}/${launcherThemeSession.themeID}`
            ),
        },
      ],
    },
    {
      label: 'Edit',
      items: [
        {
          label: 'Undo',
          rightSlot: 'ctrl ⌘ + z',
          onClick: () => scene.historyUndo(),
        },
        {
          label: 'Redo',
          rightSlot: 'ctrl ⌘ + y',
          onClick: () => scene.historyRedo(),
        },
        {
          label: 'Delete Selected Element',
          rightSlot: 'del',
          onClick: () => scene.deleteSelectedElements(),
        },
        {
          label: 'Duplicate Selected Element',
          rightSlot: 'ctrl ⌘ + shift + d',
          onClick: () => scene.duplicateSelectedElement(),
        },
      ],
    },
    {
      label: 'Insert',
      items: [
        {
          label: 'Box',
          onClick: () => scene.addLauncherElement('div'),
        },
        {
          label: 'Button',
          onClick: () => scene.addLauncherElement('button'),
        },
        {
          label: 'Text',
          onClick: () => scene.addLauncherElement('text'),
        },
        {
          label: 'Hyperlink Text',
          onClick: () => scene.addLauncherElement('link'),
        },
        {
          label: 'Variable Text',
          onClick: () => scene.addLauncherElement('variableText'),
        },
        {
          label: 'Image',
          onClick: () => scene.addLauncherElement('img'),
        },
        {
          label: 'Progressbar',
          onClick: () => scene.addLauncherElement('appTaskProgressBar'),
        },
        {
          label: 'Iframe',
          onClick: () => scene.addLauncherElement('iframe'),
        },
      ],
    },
    {
      label: 'Help',
      items: [
        {
          label: 'Support',
          onClick: () => {
            window.open('https://patchkit.net/support.html');
          },
        },
        {
          label: 'Quick Start Guide',
          onClick: () => {
            localStorage.removeItem('wasOboardingModalClosed');
            setUI((UI) => ({ ...UI, isOnboardingModalOpen: true }));
          },
        },
      ],
    },
  ];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          const fileContent = e.target.result as string;
          scene.importLauncherConfigJSON(fileContent);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Container>
      <input
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={(e) => handleFileChange(e)}
      />
      <TopbarMenu data={menuData} />
      <SideIcons>{userSession.isLoggedIn && <TopbarButtons />}</SideIcons>
    </Container>
  );
};

const Container = styled.div`
  user-select: none;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 3px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  & > div {
    display: flex;
  }
`;

const SideIcons = styled.div`
  display: flex;
  align-items: center;
`;
